<template>
    <div id="supervisor-dashboard">
        <b-overlay :show="showLoading" spinner-variant="primary">

            <b-card no-body>
                <b-card-header>
                    <b-row class="align-items-end w-100">
                        <b-col cols="12" sm="4" md="3">
                            <label class="mb-25 font-weight-bold">Date Début</label>
                            <b-form-datepicker v-model="startDate" size="sm" :max="endDate" />
                        </b-col>
                        <b-col cols="12" sm="4" md="3" class="mt-1 mt-sm-0">
                            <label class="mb-25 font-weight-bold">Date Fin</label>
                            <b-form-datepicker v-model="endDate" size="sm" :min="startDate" :max="new Date()" />
                        </b-col>
                        <b-col cols="12" sm="4" md="2" class="mt-1 mt-sm-0 d-flex align-items-end">
                            <b-button variant="primary" size="sm" class="w-100" @click="loadData" :disabled="showLoading">
                                <feather-icon icon="RefreshCwIcon" size="14" class="mr-50" :class="showLoading ? 'spinner' : ''" />
                                Actualiser
                            </b-button>
                        </b-col>
                        <b-col cols="12" md="4" class="mt-1 mt-md-0 d-flex align-items-end justify-content-md-end">
                            <b-button-group size="sm">
                                <b-button :variant="portalOnly ? 'outline-secondary' : 'primary'" @click="setMode(false)" :disabled="showLoading">
                                    <feather-icon icon="GridIcon" size="14" class="mr-50" />
                                    Toutes les commandes
                                </b-button>
                                <b-button :variant="portalOnly ? 'primary' : 'outline-secondary'" @click="setMode(true)" :disabled="showLoading">
                                    <feather-icon icon="MonitorIcon" size="14" class="mr-50" />
                                    Portail uniquement
                                </b-button>
                            </b-button-group>
                        </b-col>
                    </b-row>
                </b-card-header>

                <b-card-body>

                    <!-- KPI Summary Row -->
                    <b-row class="mb-2">
                        <b-col md="3" sm="6" class="mb-2 mb-md-0">
                            <b-card class="text-center mb-0" bg-variant="primary" text-variant="white">
                                <feather-icon icon="ShoppingCartIcon" size="28" class="mb-1" />
                                <h2 class="font-weight-bolder text-white">{{ stats.totalOrders }}</h2>
                                <b-card-text class="text-white">Commandes Portail</b-card-text>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-2 mb-md-0">
                            <b-card class="text-center mb-0" bg-variant="success" text-variant="white">
                                <feather-icon icon="DollarSignIcon" size="28" class="mb-1" />
                                <h2 class="font-weight-bolder text-white">{{ stats.totalRevenue }}</h2>
                                <b-card-text class="text-white">Chiffre d'Affaires</b-card-text>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-2 mb-md-0">
                            <b-card class="text-center mb-0" bg-variant="warning" text-variant="white">
                                <feather-icon icon="UsersIcon" size="28" class="mb-1" />
                                <h2 class="font-weight-bolder text-white">{{ stats.uniquePharmacies }}</h2>
                                <b-card-text class="text-white">Officines Actives</b-card-text>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-2 mb-md-0">
                            <b-card class="text-center mb-0" bg-variant="info" text-variant="white">
                                <feather-icon icon="TrendingUpIcon" size="28" class="mb-1" />
                                <h2 class="font-weight-bolder text-white">{{ stats.avgOrderAmount }}</h2>
                                <b-card-text class="text-white">Panier Moyen</b-card-text>
                            </b-card>
                        </b-col>
                    </b-row>

                    <!-- Charts -->
                    <section :key="showDataKeys">
                        <b-row class="match-height">
                            <b-col lg="6" sm="12">
                                <b-card no-body class="card-revenue-budget">
                                    <b-card-header>
                                        <b-col lg="12" sm="12">
                                            <h3>Top 10 Officines — Nombre de Commandes</h3>
                                        </b-col>
                                    </b-card-header>
                                    <vue-apex-charts height="400" :options="pieChartNumbers"
                                        :series="pieChartNumbers.series" />
                                </b-card>
                            </b-col>
                            <b-col lg="6" sm="12">
                                <b-card no-body class="card-revenue-budget">
                                    <b-card-header>
                                        <b-col lg="12" sm="12">
                                            <h3>Top 10 Officines — Chiffre d'Affaires</h3>
                                        </b-col>
                                    </b-card-header>
                                    <vue-apex-charts height="400" :options="pieChartAmount"
                                        :series="pieChartAmount.series" />
                                </b-card>
                            </b-col>
                            <b-col lg="12" sm="12">
                                <b-card no-body class="card-revenue-budget">
                                    <b-card-header>
                                        <b-row class="w-100 align-items-center no-gutters">
                                            <b-col lg="8" sm="12">
                                                <h3>{{ columnChartNumbersTitle }}</h3>
                                            </b-col>
                                            <b-col lg="4" sm="12">
                                                <b-form-select v-model="comparisonModeNumbers"
                                                    :options="comparisonOptions" size="sm" />
                                            </b-col>
                                        </b-row>
                                    </b-card-header>
                                    <b-alert :show="comparisonModeNumbers === 'month' && rangeDays >= 50"
                                        variant="warning" class="mx-2 p-1">
                                        Période de plus de 50 jours : les données sont regroupées par mois et chaque mois
                                        est comparé au mois qui le précède (ex. août 2026 vs juillet 2026). Les deux
                                        séries se chevauchent donc en grande partie sur la période sélectionnée — pour ce
                                        type de période, la comparaison avec l'année précédente (N-1) est généralement
                                        plus pertinente.
                                    </b-alert>
                                    <b-alert :show="comparisonModeNumbers === 'month' && rangeDays < 50" variant="info"
                                        class="mx-2 p-1">
                                        Chaque jour est comparé au même jour du mois précédent (ex. 02/08/2026 vs
                                        02/07/2026). Les jours sans équivalent dans le mois précédent (ex. le 31/07 n'a
                                        pas d'équivalent en juin) sont affichés sans valeur de comparaison.
                                    </b-alert>
                                    <vue-apex-charts type="line" height="400"
                                        :options="columnChartNumbers.chartOptions"
                                        :series="columnChartNumbers.series" />
                                </b-card>
                            </b-col>
                            <b-col lg="12" sm="12">
                                <b-card no-body class="card-revenue-budget">
                                    <b-card-header>
                                        <b-row class="w-100 align-items-center no-gutters">
                                            <b-col lg="8" sm="12">
                                                <h3>{{ columnChartAmountTitle }}</h3>
                                            </b-col>
                                            <b-col lg="4" sm="12">
                                                <b-form-select v-model="comparisonModeAmount"
                                                    :options="comparisonOptions" size="sm" />
                                            </b-col>
                                        </b-row>
                                    </b-card-header>
                                    <b-alert :show="comparisonModeAmount === 'month' && rangeDays >= 50"
                                        variant="warning" class="mx-2 p-1">
                                        Période de plus de 50 jours : les données sont regroupées par mois et chaque mois
                                        est comparé au mois qui le précède (ex. août 2026 vs juillet 2026). Les deux
                                        séries se chevauchent donc en grande partie sur la période sélectionnée — pour ce
                                        type de période, la comparaison avec l'année précédente (N-1) est généralement
                                        plus pertinente.
                                    </b-alert>
                                    <b-alert :show="comparisonModeAmount === 'month' && rangeDays < 50" variant="info"
                                        class="mx-2 p-1">
                                        Chaque jour est comparé au même jour du mois précédent (ex. 02/08/2026 vs
                                        02/07/2026). Les jours sans équivalent dans le mois précédent (ex. le 31/07 n'a
                                        pas d'équivalent en juin) sont affichés sans valeur de comparaison.
                                    </b-alert>
                                    <vue-apex-charts type="bar" height="400"
                                        :options="columnChartAmount.chartOptions"
                                        :series="columnChartAmount.series" />
                                </b-card>
                            </b-col>
                        </b-row>
                    </section>

                </b-card-body>
            </b-card>

        </b-overlay>
    </div>
</template>

<script>
import { kFormatter } from '@core/utils/filter'
import { $themeColors } from "@themeConfig";
import VueApexCharts from "vue-apexcharts";

export default {
    components: {
        VueApexCharts,
    },
    data() {
        const vm = this;
        return {
            portalOnly: true,
            columnChartNumbersTitle: "Variation Quotidienne des Ventes — Nombre de Commandes",
            columnChartAmountTitle: "Variation Quotidienne des Ventes — Chiffre d'Affaires",
            comparisonModeNumbers: 'none',
            comparisonModeAmount: 'none',
            comparisonOptions: [
                { value: 'none', text: 'Sans comparaison' },
                { value: 'year', text: 'Comparer avec l\'année précédente (N-1)' },
                { value: 'month', text: 'Comparer avec le mois précédent (M-1)' }
            ],
            currentOrders: [],
            comparisonOrders: { year: null, month: null },
            tooltipDatesNumbers: { current: [], comparison: [] },
            tooltipDatesAmount: { current: [], comparison: [] },
            loadedStartDate: '',
            loadedEndDate: '',
            stats: {
                totalOrders: 0,
                totalRevenue: '0',
                uniquePharmacies: 0,
                avgOrderAmount: '0',
            },
            columnChartNumbers: {
                series: [
                    { name: 'Période', data: [] },
                ],
                chartOptions: {
                    colors: [$themeColors.primary, $themeColors.secondary],
                    dataLabels: { enabled: false },
                    stroke: { width: 5, curve: 'smooth', show: true },
                    xaxis: { categories: [] },
                    yaxis: {
                        labels: {
                            formatter: value => kFormatter(value)
                        }
                    },
                    tooltip: {
                        custom(ctx) {
                            return vm.renderComparisonTooltip(ctx, 'numbers');
                        }
                    }
                }
            },
            columnChartAmount: {
                series: [
                    { name: 'Montant (DT)', data: [] },
                ],
                chartOptions: {
                    colors: [$themeColors.success, $themeColors.secondary],
                    plotOptions: {
                        bar: { horizontal: false, endingShape: 'rounded', columnWidth: '45%' }
                    },
                    dataLabels: { enabled: false },
                    stroke: { show: true, width: 2, colors: ['transparent'] },
                    xaxis: { categories: [] },
                    yaxis: {
                        labels: {
                            formatter: value => kFormatter(value)
                        }
                    },
                    fill: { opacity: 1 },
                    tooltip: {
                        custom(ctx) {
                            return vm.renderComparisonTooltip(ctx, 'amount');
                        }
                    }
                }
            },
            pieChartNumbers: {
                series: [],
                chart: { width: 380, type: 'pie' },
                labels: [],
                responsive: [{ breakpoint: 480, options: { chart: { width: 200 }, legend: { position: 'bottom' } } }],
                tooltip: { y: { formatter: val => kFormatter(val) } }
            },
            pieChartAmount: {
                series: [],
                chart: { width: 380, type: 'pie' },
                labels: [],
                responsive: [{ breakpoint: 480, options: { chart: { width: 200 }, legend: { position: 'bottom' } } }],
                tooltip: {
                    y: {
                        formatter: val => parseFloat(val).toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,') + ' DT'
                    }
                }
            },
            showDataKeys: 0,
            showLoading: true,
            startDate: '',
            endDate: '',
        }
    },
    computed: {
        rangeDays() {
            if (!this.loadedStartDate || !this.loadedEndDate) return 0;
            return Math.floor((new Date(this.loadedEndDate) - new Date(this.loadedStartDate)) / (1000 * 60 * 60 * 24));
        }
    },
    watch: {
        comparisonModeNumbers() {
            this.onComparisonModeChange();
        },
        comparisonModeAmount() {
            this.onComparisonModeChange();
        }
    },
    created() {
        const { pastDay, currentDay } = this.getCurrentAndPastDay(5);
        this.startDate = pastDay;
        this.endDate = currentDay;
        this.loadData();
    },
    methods: {
        async loadData() {
            this.showLoading = true;
            // the cached comparison data belongs to the previous range / mode
            this.comparisonOrders = { year: null, month: null };
            this.loadedStartDate = this.startDate;
            this.loadedEndDate = this.endDate;

            let response = await this.$http.post("order_header/findWhere", this.generateDateFilters(this.startDate, this.endDate)).catch(() => null);
            this.currentOrders = response ? response.data : [];
            const data = this.currentOrders;

            // KPI stats
            const totalOrders = data.length;
            const totalRevenue = data.reduce((sum, o) => sum + parseFloat(o.Amount_Including_VAT || 0), 0);
            const uniquePharmacies = new Set(data.map(o => o.Sell_to_Customer_No)).size;
            const avgOrderAmount = totalOrders > 0 ? totalRevenue / totalOrders : 0;
            this.stats.totalOrders = totalOrders;
            this.stats.totalRevenue = this.fmtAmount(totalRevenue);
            this.stats.uniquePharmacies = uniquePharmacies;
            this.stats.avgOrderAmount = this.fmtAmount(avgOrderAmount);

            // Pie charts
            const top10ByOrders = this.getTop10ClientsByOrders(data);
            this.pieChartNumbers.series = top10ByOrders.data;
            this.pieChartNumbers.labels = top10ByOrders.labels;
            const top10ByAmount = this.getTop10ClientsByAmount(data);
            this.pieChartAmount.series = top10ByAmount.data;
            this.pieChartAmount.labels = top10ByAmount.labels;

            await this.refreshComparisons();
            this.showLoading = false;
        },

        async onComparisonModeChange() {
            this.showLoading = true;
            await this.refreshComparisons();
            this.showLoading = false;
        },

        async refreshComparisons() {
            const modes = new Set([this.comparisonModeNumbers, this.comparisonModeAmount]);
            modes.delete('none');
            for (const mode of modes) {
                if (!this.comparisonOrders[mode]) {
                    const start = this.shiftDayKeyClamped(this.loadedStartDate, mode);
                    const end = this.shiftDayKeyClamped(this.loadedEndDate, mode);
                    const response = await this.$http.post("order_header/findWhere", this.generateDateFilters(start, end)).catch(() => null);
                    this.comparisonOrders[mode] = response ? response.data : [];
                }
            }
            this.rebuildCharts();
        },

        rebuildCharts() {
            const daily = this.rangeDays < 50;
            let keys, labels;
            if (daily) {
                this.columnChartNumbersTitle = "Variation Quotidienne des Ventes — Nombre de Commandes";
                this.columnChartAmountTitle = "Variation Quotidienne des Ventes — Chiffre d'Affaires";
                keys = this.listDaysBetween(this.loadedStartDate, this.loadedEndDate);
                labels = keys.map(k => new Date(k).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }));
            } else {
                this.columnChartNumbersTitle = "Variation Mensuelle des Ventes — Nombre de Commandes";
                this.columnChartAmountTitle = "Variation Mensuelle des Ventes — Chiffre d'Affaires";
                keys = this.listMonthsBetween(this.loadedStartDate, this.loadedEndDate);
                labels = keys.map(k => this.formatMonthLabel(k));
            }
            const currentGrouped = daily ? this.groupOrdersByDay(this.currentOrders) : this.groupOrdersByMonth(this.currentOrders);
            this.buildComparableChart(this.columnChartNumbers, this.tooltipDatesNumbers, 'Période', 'count', currentGrouped, this.comparisonModeNumbers, daily, keys, labels);
            this.buildComparableChart(this.columnChartAmount, this.tooltipDatesAmount, 'Montant (DT)', 'amount', currentGrouped, this.comparisonModeAmount, daily, keys, labels);
            this.showDataKeys += 1;
        },

        buildComparableChart(chart, tooltipDates, baseName, field, currentGrouped, mode, daily, keys, labels) {
            const readValue = group => {
                if (!group) return 0;
                return field === 'amount' ? parseFloat(group.amount.toFixed(2)) : group.count;
            };
            chart.chartOptions.xaxis.categories = labels;
            tooltipDates.current = keys.map(k => daily ? this.formatDayFr(k) : this.formatMonthLabel(k));
            const series = [{
                name: baseName,
                data: keys.map(k => readValue(currentGrouped[k]))
            }];
            if (mode !== 'none') {
                const compGrouped = daily ? this.groupOrdersByDay(this.comparisonOrders[mode] || []) : this.groupOrdersByMonth(this.comparisonOrders[mode] || []);
                const shiftedKeys = keys.map(k => daily ? this.shiftDayKey(k, mode) : this.shiftMonthKey(k, mode));
                series.push({
                    name: mode === 'year' ? 'Année précédente (N-1)' : 'Mois précédent (M-1)',
                    data: shiftedKeys.map(sk => sk === null ? null : readValue(compGrouped[sk]))
                });
                tooltipDates.comparison = shiftedKeys.map(sk => {
                    if (sk === null) return null;
                    return daily ? this.formatDayFr(sk) : this.formatMonthLabel(sk);
                });
            } else {
                tooltipDates.comparison = [];
            }
            chart.series = series;
        },

        renderComparisonTooltip({ series, dataPointIndex, w }, chartKey) {
            const dates = chartKey === 'numbers' ? this.tooltipDatesNumbers : this.tooltipDatesAmount;
            const isAmount = chartKey === 'amount';
            let html = '<div style="padding: 8px 12px;">';
            series.forEach((s, i) => {
                const dateLabel = i === 0 ? dates.current[dataPointIndex] : dates.comparison[dataPointIndex];
                const rawValue = s[dataPointIndex];
                let valueLabel;
                if (rawValue === null || rawValue === undefined || dateLabel === null) {
                    valueLabel = '—';
                } else if (isAmount) {
                    valueLabel = parseFloat(rawValue).toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,') + ' DT';
                } else {
                    valueLabel = parseFloat(rawValue).toFixed(0);
                }
                const color = (w.globals.colors && w.globals.colors[i]) || '#888';
                const name = (w.globals.seriesNames && w.globals.seriesNames[i]) || '';
                html += '<div style="margin: 2px 0;">'
                    + '<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:' + color + ';margin-right:6px;"></span>'
                    + '<strong>' + name + '</strong>'
                    + (dateLabel ? ' (' + dateLabel + ')' : '')
                    + ' : ' + valueLabel
                    + '</div>';
            });
            return html + '</div>';
        },

        getTop10ClientsByOrders(list) {
            const counts = {};
            list.forEach(o => {
                const name = o.Sell_to_Customer_No || o.Bill_to_Name || 'Inconnu';
                counts[name] = (counts[name] || 0) + 1;
            });
            const top10 = Object.keys(counts).sort((a, b) => counts[b] - counts[a]).slice(0, 10);
            return { labels: top10, data: top10.map(c => counts[c]) };
        },

        getTop10ClientsByAmount(list) {
            const amounts = {};
            list.forEach(o => {
                const name = o.Sell_to_Customer_No || o.Bill_to_Name || 'Inconnu';
                amounts[name] = (amounts[name] || 0) + parseFloat(o.Amount_Including_VAT || 0);
            });
            const top10 = Object.keys(amounts).sort((a, b) => amounts[b] - amounts[a]).slice(0, 10);
            return { labels: top10, data: top10.map(c => parseFloat(amounts[c].toFixed(2))) };
        },

        groupOrdersByDay(list) {
            const grouped = {};
            list.forEach(o => {
                const key = ('' + o.Posting_Date).split('T')[0];
                if (!grouped[key]) grouped[key] = { count: 0, amount: 0 };
                grouped[key].count++;
                grouped[key].amount += parseFloat(o.Amount_Including_VAT || 0);
            });
            return grouped;
        },

        groupOrdersByMonth(list) {
            const grouped = {};
            list.forEach(o => {
                const key = ('' + o.Posting_Date).split('T')[0].slice(0, 7);
                if (!grouped[key]) grouped[key] = { count: 0, amount: 0 };
                grouped[key].count++;
                grouped[key].amount += parseFloat(o.Amount_Including_VAT || 0);
            });
            return grouped;
        },

        listDaysBetween(startString, endString) {
            const days = [];
            const current = new Date(startString);
            const end = new Date(endString);
            while (current <= end) {
                days.push(`${current.getFullYear()}-${(current.getMonth() + 1 + "").padStart(2, "0")}-${(current.getDate() + "").padStart(2, "0")}`);
                current.setDate(current.getDate() + 1);
            }
            return days;
        },

        listMonthsBetween(startString, endString) {
            const months = [];
            const start = new Date(startString);
            const end = new Date(endString);
            let year = start.getFullYear();
            let month = start.getMonth() + 1;
            const endYear = end.getFullYear();
            const endMonth = end.getMonth() + 1;
            while (year < endYear || (year === endYear && month <= endMonth)) {
                months.push(`${year}-${(month + "").padStart(2, "0")}`);
                month++;
                if (month > 12) {
                    month = 1;
                    year++;
                }
            }
            return months;
        },

        // '2026-08-02' shifted one year or one month back; null when the target day
        // does not exist (e.g. 31/07 -> 31/06, 29/02 outside leap years)
        shiftDayKey(dayKey, mode) {
            const [year, month, day] = dayKey.split('-').map(Number);
            let targetYear, targetMonth;
            if (mode === 'year') {
                targetYear = year - 1;
                targetMonth = month;
            } else {
                targetYear = month === 1 ? year - 1 : year;
                targetMonth = month === 1 ? 12 : month - 1;
            }
            const lastDay = new Date(targetYear, targetMonth, 0).getDate();
            if (day > lastDay) return null;
            return `${targetYear}-${(targetMonth + "").padStart(2, "0")}-${(day + "").padStart(2, "0")}`;
        },

        // same shift but clamped to the last existing day, for the fetch window bounds
        shiftDayKeyClamped(dayKey, mode) {
            const [year, month, day] = dayKey.split('-').map(Number);
            let targetYear, targetMonth;
            if (mode === 'year') {
                targetYear = year - 1;
                targetMonth = month;
            } else {
                targetYear = month === 1 ? year - 1 : year;
                targetMonth = month === 1 ? 12 : month - 1;
            }
            const lastDay = new Date(targetYear, targetMonth, 0).getDate();
            const targetDay = Math.min(day, lastDay);
            return `${targetYear}-${(targetMonth + "").padStart(2, "0")}-${(targetDay + "").padStart(2, "0")}`;
        },

        // '2026-08' -> '2025-08' (year) or '2026-07' (month)
        shiftMonthKey(monthKey, mode) {
            const [year, month] = monthKey.split('-').map(Number);
            if (mode === 'year') {
                return `${year - 1}-${(month + "").padStart(2, "0")}`;
            }
            const targetYear = month === 1 ? year - 1 : year;
            const targetMonth = month === 1 ? 12 : month - 1;
            return `${targetYear}-${(targetMonth + "").padStart(2, "0")}`;
        },

        formatDayFr(dayKey) {
            const [year, month, day] = dayKey.split('-');
            return `${day}/${month}/${year}`;
        },

        formatMonthLabel(monthKey) {
            const [year, month] = monthKey.split('-').map(Number);
            return new Date(year, month - 1, 1).toLocaleString('fr-FR', { year: 'numeric', month: 'long' });
        },

        generateDateFilters(startDate, endDate) {
            const s = new Date(startDate); s.setDate(s.getDate() - 1);
            const e = new Date(endDate); e.setDate(e.getDate() + 1);
            const filters = [
                { field: "Posting_Date", value: s.toISOString().split('T')[0], operator: "gt" },
                { field: "Posting_Date", value: e.toISOString().split('T')[0], operator: "lt" },
            ];
            if (this.portalOnly) filters.push({ field: "Web_Order", value: true, operator: "eq" });
            return filters;
        },

        setMode(portalOnly) {
            if (this.portalOnly === portalOnly) return;
            this.portalOnly = portalOnly;
            this.loadData();
        },

        fmtAmount(value) {
            return parseFloat(value).toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' DT';
        },

        getCurrentAndPastDay(days) {
            const currentDate = new Date();
            const pastDate = new Date();
            pastDate.setDate(pastDate.getDate() - days);
            const fmt = d => `${d.getFullYear()}-${(d.getMonth() + 1 + '').padStart(2, '0')}-${(d.getDate() + '').padStart(2, '0')}`;
            return { currentDay: fmt(currentDate), pastDay: fmt(pastDate) };
        },
    }
}
</script>

<style lang="scss"></style>
